import Table, {
  Props as TableProps,
  buildExpandableCountMultiColumn,
  buildTagColumn,
  buildCopyTextColumn
} from "@/components/Table";
import TableDetailsPanel from "@/components/TableDetailsPanel";
import { toFormat } from "@/lib/dateHelpers";
import { ColumnDef, createColumnHelper, Row } from "@tanstack/react-table";
import { ReactElement, useCallback, useState } from "react";
import { ChangeRequestStatus, Node } from "@/pages/sections/[sectionId]/change-requests/queries";
import EmptyState, { Props as EmptyStateProps } from "@/components/EmptyState";
import Icon from "@/components/Icon";
import IconButton from "@/components/IconButton";
import Tooltip from "@/components/Tooltip";
import {
  changeFieldLabel,
  formatChangeRequestType,
  formatStage,
  FormattedChange,
  getVisibleChangeCount,
  visibleChanges
} from "@/pages/sections/[sectionId]/change-requests/_utils";
import NotesTable, { type NoteEditingConfig } from "@/features/global/components/NotesTable";
import type { CRMNote } from "@/types";
import type { TagType } from "@/components/Tag/Tag";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";

type OnEditChangeField = (node: Node, field: FormattedChange) => (() => void) | undefined;

const getCrmNotes = ({ crmNotes }: Node) => crmNotes;

const formatRequestType = formatChangeRequestType;

const Change = ({ node, onEdit }: { node: Node; onEdit?(field: FormattedChange): ReturnType<OnEditChangeField> }) => {
  const { accessor, display } = createColumnHelper<FormattedChange>();

  const columns = [
    display({
      id: "label",
      header: "Type of Data",
      cell({ row }) {
        return changeFieldLabel(row.original.id);
      }
    }),
    accessor("existing", { header: "Existing Value" }),
    accessor("requested", {
      header: "Requested Value",
      cell: ({ getValue, row }) => {
        const value = getValue();
        const editClick = onEdit?.(row.original);

        if (value) {
          return (
            <div className="flex items-center justify-between gap-2">
              <span>{value}</span>
              {editClick && (
                <IconButton
                  name="edit"
                  title={`Edit ${changeFieldLabel(row.original.id)}`}
                  onClick={editClick}
                  className="!m-0"
                />
              )}
            </div>
          );
        }

        return (
          <div className="flex items-center justify-between gap-2">
            {editClick ? (
              <Tooltip
                variant="plain"
                color="black"
                supportingText={`${changeFieldLabel(row.original.id)} needs to be set`}
                menuCorner="end-start"
                anchorCorner="start-start"
              >
                <div className="flex items-center">
                  <Icon className="text-extended-warning-brand-color" name="emergency_home" />
                  <span className="ml-1 opacity-20">--</span>
                </div>
              </Tooltip>
            ) : (
              <span className="opacity-20">--</span>
            )}
            {editClick && (
              <IconButton
                name="edit"
                title={`Edit ${changeFieldLabel(row.original.id)}`}
                onClick={editClick}
                className="!m-0"
              />
            )}
          </div>
        );
      }
    })
  ];

  const filteredData = visibleChanges(node.change, node.status);

  return (
    <TableDetailsPanel
      data={filteredData}
      columns={columns as ColumnDef<FormattedChange>[]}
      title={`Changes (${getVisibleChangeCount(node.change, node.status)})`}
    />
  );
};

type NoteEditingBase = Omit<NoteEditingConfig, "notableId"> & { pgaId?: string };

const Notes = ({
  node,
  highlightNoteId,
  noteEditing
}: {
  node: Node;
  highlightNoteId?: string | null;
  noteEditing?: NoteEditingBase;
}) => (
  <div data-testid="notes">
    <NotesTable
      variant="details-panel"
      data={getCrmNotes(node)}
      title={`Notes (${getNotesCount(getCrmNotes(node))})`}
      highlightNoteId={highlightNoteId}
      noteEditing={
        noteEditing ? { ...noteEditing, notableId: node.id, pgaId: noteEditing.pgaId || node.person?.pgaId } : undefined
      }
    />
  </div>
);

const getNotesCount = (crmNotes: CRMNote[]) => crmNotes.length;

const { accessor } = createColumnHelper<Node>();

const stageTagType: Record<ChangeRequestStatus, TagType> = {
  [ChangeRequestStatus.PENDING]: "warn",
  [ChangeRequestStatus.CANCELED]: "surface",
  [ChangeRequestStatus.REJECTED]: "error",
  [ChangeRequestStatus.APPROVED]: "success"
};

function isEmploymentDeleted({ change }: Node): boolean {
  switch (change.__typename) {
    case "BackofficeUpdateUnlistedEmploymentChange":
    case "BackofficeUpdateEmploymentChange":
      return !!change.employmentDeletedAt;
    default:
      return false;
  }
}

type ExpansionState = Record<string, string | null>;

type NoteDeepLinkContext = {
  expandNotesRowId: string | null;
  highlightNoteId: string | null;
};

type ChangeDeepLinkContext = {
  expandChangesRowId: string | null;
};

const columnDefs = {
  submittedAt: () =>
    accessor("submittedAt", {
      header: "Submitted",
      enableSorting: false,
      cell: ({ getValue }) => toFormat(getValue())
    }),
  type: () =>
    accessor("change.__typename", {
      header: "Request Type",
      enableSorting: false,
      size: 220,
      minSize: 180,
      cell: ({ getValue }) => formatRequestType(getValue())
    }),
  pgaId: () =>
    buildCopyTextColumn<Node>({
      accessorKey: "person.pgaId",
      header: "PGA ID",
      copyFieldLabel: "PGA ID",
      getText: (row) => row.person.pgaId,
      enableSorting: false
    }),
  fullName: () =>
    accessor(({ person }) => `${person.firstName} ${person.lastName}`, {
      id: "name",
      enableSorting: false,
      header: "Name"
    }),
  change: (
    expanded: ExpansionState,
    onExpand: (rowId: string, panelId: string | null) => void,
    deepLink?: ChangeDeepLinkContext | null
  ) =>
    buildExpandableCountMultiColumn<Node>({
      panelId: "change",
      accessorKey: "change",
      header: "Changes",
      getCount: (row) => getVisibleChangeCount(row.change, row.status),
      expanded,
      onExpand,
      autoExpandRowId: deepLink?.expandChangesRowId ?? undefined
    }),
  notes: (
    expanded: ExpansionState,
    onExpand: (rowId: string, panelId: string | null) => void,
    deepLink?: NoteDeepLinkContext | null
  ) =>
    buildExpandableCountMultiColumn<Node>({
      panelId: "notes",
      id: "notes",
      header: "Notes",
      getCount: (row) => getNotesCount(getCrmNotes(row)),
      expanded,
      onExpand,
      autoExpandRowId: deepLink?.expandNotesRowId ?? undefined
    }),
  status: () =>
    buildTagColumn<Node>({
      accessorKey: "status",
      header: "Stage",
      getTagLabel: (row) => formatStage(row.status),
      getTagType: (row) => stageTagType[row.status],
      getIconName: (row) => (isEmploymentDeleted(row) ? "error" : undefined),
      getTooltipText: (row) => (isEmploymentDeleted(row) ? "Employment has been deleted" : undefined)
    })
};
type ChangeRequestColumn = keyof typeof columnDefs | ColumnDef<Node, any>;

function buildColumns(
  columns: ChangeRequestColumn[],
  expanded: ExpansionState,
  onExpand: (rowId: string, panelId: string | null) => void,
  noteDeepLink?: NoteDeepLinkContext | null,
  changeDeepLink?: ChangeDeepLinkContext | null
) {
  return columns.map((column) => {
    if (typeof column !== "string") return column;
    if (column === "notes") {
      return columnDefs.notes(expanded, onExpand, noteDeepLink);
    }
    if (column === "change") {
      return columnDefs.change(expanded, onExpand, changeDeepLink);
    }
    const factory = columnDefs[column];
    return factory.length >= 2
      ? (factory as (typeof columnDefs)["change"])(expanded, onExpand)
      : (factory as (typeof columnDefs)["submittedAt"])();
  });
}

interface Props extends Omit<
  TableProps<Node>,
  "columns" | "renderExpandedRow" | "renderFilteredEmptyState" | "renderNoDataEmptyState"
> {
  loading: boolean;
  data: Node[];
  renderFooter(): ReactElement;
  manageChangeRequests?: boolean;
  onHorizontalScroll?(): void;
  emptyCaption: string;
  emptyTitle?: string;
  columns: ChangeRequestColumn[];
  onEdit?: OnEditChangeField;
  emptyAction?: EmptyStateProps["action"];
  noteEditing?: NoteEditingBase;
  /** From person notes deep link: `?expandNotes=<changeRequestId>&highlightNote=<noteId>` */
  deepLinkExpandNotesRowId?: string | null;
  deepLinkHighlightNoteId?: string | null;
  /** From section list deep link: `?expandChanges=<changeRequestId>` */
  deepLinkExpandChangesRowId?: string | null;
  highlightRowId?: string | null;
}

export function ChangeRequestTable({
  emptyAction,
  columns,
  emptyCaption,
  emptyTitle = "No Change Requests",
  manageChangeRequests,
  hasActiveFilters,
  onEdit,
  noteEditing,
  deepLinkExpandNotesRowId = null,
  deepLinkHighlightNoteId = null,
  deepLinkExpandChangesRowId = null,
  highlightRowId = null,
  ...props
}: Props) {
  const [expanded, setExpanded] = useState<ExpansionState>({});
  const { isCompactLayout } = useIsCompactLayout();

  const onExpand = useCallback((rowId: string, panelId: string | null) => {
    setExpanded((prev) => ({ ...prev, [rowId]: panelId }));
  }, []);

  const noteDeepLink: NoteDeepLinkContext | null =
    deepLinkExpandNotesRowId != null || deepLinkHighlightNoteId != null
      ? { expandNotesRowId: deepLinkExpandNotesRowId, highlightNoteId: deepLinkHighlightNoteId }
      : null;
  const changeDeepLink: ChangeDeepLinkContext | null =
    deepLinkExpandChangesRowId != null ? { expandChangesRowId: deepLinkExpandChangesRowId } : null;

  return (
    <Table
      data-testid="change-requests-table"
      renderExpandedRow={({ row: { id, original: node } }: { row: Row<Node> }) => {
        const onEdit$ = onEdit && ((change: FormattedChange) => onEdit?.(node, change));

        if (isCompactLayout) {
          return (
            <>
              <Change node={node} onEdit={onEdit$} />
              <Notes node={node} highlightNoteId={deepLinkHighlightNoteId} noteEditing={noteEditing} />
            </>
          );
        }

        switch (expanded[id]) {
          case "notes":
            return <Notes node={node} highlightNoteId={deepLinkHighlightNoteId} noteEditing={noteEditing} />;
          case "change":
            return <Change node={node} onEdit={onEdit$} />;
          default:
            return <></>;
        }
      }}
      columns={buildColumns(columns, expanded, onExpand, noteDeepLink, changeDeepLink)}
      hasActiveFilters={hasActiveFilters}
      getRowClasses={highlightRowId ? (row) => (row.original.id === highlightRowId ? "row-highlight" : "") : undefined}
      renderNoDataEmptyState={() => (
        <EmptyState title={emptyTitle} caption={emptyCaption} iconName="person" action={emptyAction} />
      )}
      {...props}
    />
  );
}
